import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type DebriefStage } from '@momentum/types';
import type { MatchPointSession, Turn } from '../types/deepDive.types';
import type { EditableFact } from '../../shared/types';
import { STAGE_META } from '../../gather-facts-stage/utils/stageMeta';
import { FactPickTurn } from './FactPickTurn';
import { QuestionTurn } from './QuestionTurn';
import { LessonTurn } from './LessonTurn';

interface TurnListProps {
  session: MatchPointSession;
  facts: EditableFact[];
  onPickFact: (factId: string) => void;
  isFirstBotTurnInChat: boolean;
}

export function TurnList({
  session,
  facts,
  onPickFact,
  isFirstBotTurnInChat,
}: TurnListProps) {
  const { t } = useTranslation('debriefV2');
  const scrollRef = useRef<HTMLDivElement>(null);
  const totalTurns = session.turns.length;
  const stageLabel = t(STAGE_META[session.stage].labelKey);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  }, [totalTurns]);

  return (
    <div
      ref={scrollRef}
      className="p-5 flex flex-col gap-4 min-h-[480px] max-h-[560px] overflow-y-auto deep-dive-chat-scroll bg-white"
    >
      {session.turns.map((turn, turnIndex) => {
        const isLatest = turnIndex === totalTurns - 1;
        const isVeryFirstBotTurn = turnIndex === 0 && isFirstBotTurnInChat;
        const showTypingDots = isLatest && !isVeryFirstBotTurn;
        return (
          <TurnRenderer
            key={`${session.stage}-${turnIndex}`}
            turn={turn}
            stage={session.stage}
            stageLabel={stageLabel}
            facts={facts}
            isActive={isLatest}
            showTypingDots={showTypingDots}
            onPickFact={onPickFact}
          />
        );
      })}
    </div>
  );
}

interface TurnRendererProps {
  turn: Turn;
  stage: DebriefStage;
  stageLabel: string;
  facts: EditableFact[];
  isActive: boolean;
  showTypingDots: boolean;
  onPickFact: (factId: string) => void;
}

function TurnRenderer({
  turn,
  stage,
  stageLabel,
  facts,
  isActive,
  showTypingDots,
  onPickFact,
}: TurnRendererProps) {
  const { t } = useTranslation('debriefV2');

  if (turn.kind === 'fact-pick') {
    return (
      <FactPickTurn
        prompt={t('deepDive.factPickPrompt')(stageLabel)}
        facts={facts}
        stage={stage}
        pickedFactId={turn.factId}
        isActive={isActive}
        showTypingDots={showTypingDots}
        onPick={onPickFact}
      />
    );
  }
  if (turn.kind === 'question') {
    return (
      <QuestionTurn
        question={t('deepDive.question')(turn.questionIndex)}
        answer={turn.answer}
        showTypingDots={showTypingDots && !turn.answer}
      />
    );
  }
  return (
    <LessonTurn
      prompt={t('deepDive.lessonsPrompt')(stageLabel)}
      lessons={turn.lessons}
      showTypingDots={showTypingDots && turn.lessons.length === 0}
    />
  );
}
